import { useLingui } from '@lingui/react/macro';
import { ArrowLeft, Target, Users } from 'lucide-react';
import { Player, Team } from 'modules/client/tournaments/domain/types';
import getFormIcon from 'modules/client/tournaments/domain/utils/get-form-icon';
import { getPlayerPositionLabel } from 'modules/teams/domain/constants/player-position-labels';
import React, { useState } from 'react';
import UDCard from 'ud-ui/components/card';

interface TeamsSectionProps {
  teams: Team[];
  players: Player[];
}

const TeamsSection: React.FC<TeamsSectionProps> = ({ teams, players }) => {
  const { t } = useLingui();
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);

  const positionName = (position: string) => {
    const label = getPlayerPositionLabel(position);
    return label ? t(label) : position;
  };

  const getTeamPlayers = (teamId: number) => {
    return players.filter((player) => player.teamId === teamId);
  };

  const getTopScorer = (teamId: number) => {
    const teamPlayers = getTeamPlayers(teamId);
    const player = teamPlayers.reduce((top, player) => (player.goals > (top?.goals || 0) ? player : top), teamPlayers[0]);
    const name = player.name.split(' ');
    return { ...player, name: `${name[0]} ${name[1]}` };
  };

  if (selectedTeam) {
    const teamPlayers = getTeamPlayers(selectedTeam.id);
    console.log({ length: teamPlayers.length });

    return (
      <div className="space-y-6">
        <button
          onClick={() => setSelectedTeam(null)}
          className="flex-center gap-2 font-medium text-emerald-600 hover:text-emerald-800"
        >
          <ArrowLeft size={16} />
          <span>Назад к командам</span>
        </button>

        <UDCard variant="elevated" padding="none" className="overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-6 py-8 text-white">
            <div className="flex items-center space-x-4">
              <span className="text-4xl">
                <img src={selectedTeam.logo} alt="logo" className="size-14 rounded-lg object-contain" />
              </span>
              <div>
                <h1 className="text-2xl font-bold">{selectedTeam.name}</h1>
                <p className="text-emerald-100">Статистика сезона</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-4">
              <div className="rounded-lg bg-emerald-50 p-4 text-center">
                <div className="text-2xl font-bold text-emerald-600">{selectedTeam.points}</div>
                <div className="text-sm text-gray-600">Очки</div>
              </div>
              <div className="rounded-lg bg-blue-50 p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">{selectedTeam.won}</div>
                <div className="text-sm text-gray-600">Победы</div>
              </div>
              <div className="rounded-lg bg-yellow-50 p-4 text-center">
                <div className="text-2xl font-bold text-yellow-600">{selectedTeam.goalsFor}</div>
                <div className="text-sm text-gray-600">Забито голов</div>
              </div>
              <div className="rounded-lg bg-red-50 p-4 text-center">
                <div className="text-2xl font-bold text-red-600">{selectedTeam.goalsAgainst}</div>
                <div className="text-sm text-gray-600">Голов пропущено</div>
              </div>
            </div>

            <h3 className="mb-4 text-lg font-bold text-gray-900">Команда</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Игроки</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Позиция</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Голов</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Помощи</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Карточки</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {teamPlayers.map((player) => (
                    <tr key={player.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{player.name}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-700">
                          {positionName(player.position ?? 'forward')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="font-medium text-emerald-600">{player.goals}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="font-medium text-blue-600">{player.assists}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center space-x-1">
                          {player.yellowCards > 0 && (
                            <span className="rounded bg-yellow-400 px-1 py-0.5 text-xs text-white">{player.yellowCards}</span>
                          )}
                          {player.redCards > 0 && (
                            <span className="rounded bg-red-600 px-1 py-0.5 text-xs text-white">{player.redCards}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </UDCard>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <UDCard variant="elevated" padding="none" className="overflow-hidden">
        <div className="bg-emerald-600 px-6 py-4 text-white">
          <h2 className="flex items-center space-x-2 text-xl font-bold">
            <Users size={20} />
            <span>Команды Турнира</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-2 lg:grid-cols-3">
          {teams.map((team) => {
            const topScorer = getTopScorer(team.id);
            return (
              <UDCard key={team.id} padding="lg" onClick={() => setSelectedTeam(team)}>
                <div className="mb-4 flex items-center space-x-3">
                  <span className="text-3xl">
                    <img src={team.logo} alt="logo" className="size-10 rounded-lg object-contain" />
                  </span>
                  <div>
                    <h3 className="font-bold text-gray-900">{team.name}</h3>
                    {team.shortName !== team.name && <p className="text-sm text-gray-600">{team.shortName}</p>}
                  </div>
                </div>

                <div className="mb-4 grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-lg font-bold text-emerald-600">{team.points}</div>
                    <div className="text-xs text-gray-600">Очков</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-blue-600">{team.won}</div>
                    <div className="text-xs text-gray-600">Побед</div>
                  </div>
                </div>

                {topScorer && (
                  <div className="border-t border-gray-200 pt-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Лучший бомбардир:</span>
                      <div className="flex items-center space-x-1">
                        <Target size={12} className="text-emerald-600" />
                        <span className="font-medium">{topScorer.name}</span>
                        <span className="font-bold text-emerald-600">({topScorer.goals})</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-3">
                  <div className="mb-1 text-xs text-gray-500">Последние результаты:</div>
                  <div className="flex space-x-1">
                    {team.form.map((result, idx) => (
                      <div key={idx}>{getFormIcon(result)}</div>
                    ))}
                  </div>
                </div>
              </UDCard>
            );
          })}
        </div>
      </UDCard>
    </div>
  );
};

export default TeamsSection;
